/**
 * /best-ai-tools-for-product-managers (zh) — 中文变体。
 * 对标 "产品经理 AI 工具" 关键词族，延续英文版"诚实清单/指南"基调:
 * 不是软广,而是按 PM 实际用途分类,大方点名各类里真正强的工具
 * (ChatPRD、ChatGPT、Notion 等),只把 Draftlize 放在它真正独占的
 * 那一类:让产品决策不漂移(决策追踪 + stale 传播 + agent 可读写卡片)。
 * 诚实是这页可信度的关键 —— 呼应 comparisons/ChatGpt.tsx 那种
 * "大方承认对手长处"的调性。
 *
 * 英文变体见 BestAiToolsForProductManagers.tsx。
 */
import { MarketingShell } from './MarketingShell';

export function BestAiToolsForProductManagersZh() {
  return (
    <MarketingShell
      currentLocale="zh"
      hreflangPath="/best-ai-tools-for-product-managers"
      documentTitle="产品经理 AI 工具清单(2026):按用途诚实排序 · Draftlize"
      documentDescription="一份不灌水的产品经理 AI 工具清单(2026),按你真正的用途排序:写 PRD、头脑风暴、知识库、用户调研,以及让产品决策不漂移。大方点名各类里真正强的工具,只把 Draftlize 放在它真正独占的那一类。"
    >
      <section className="hero" style={{ paddingTop: 88, paddingBottom: 64 }}>
        <div style={{ maxWidth: 860 }}>
          <span className="label label-amber">指南 · 产品经理 AI 工具</span>
          <h1
            className="serif"
            style={{ fontSize: 'clamp(34px, 4.6vw, 56px)', lineHeight: 1.12, margin: '12px 0 24px' }}
          >
            产品经理的 AI 工具清单,<br/>
            <em>按你真正要它干什么来排。</em>
          </h1>
          <p className="lede">
            大多数"产品经理 AI 工具"榜单只是一堵 logo 墙加几个返佣链接。这篇不是。我们自己做其中一款工具,但我们会跟你直说:写 PRD、头脑风暴、做知识库、跑用户调研——每一类都有比我们更出名、更合适的选择,我们会一个个点名。Draftlize 在这页只配得上一类:让你的产品决策不悄悄漂移、不互相打架。下面是这张诚实的地图。
          </p>
        </div>
      </section>

      <section className="compare">
        <div className="head">
          <div>
            <span className="label">一句话</span>
            <h2 className="serif">没有哪一款全能。<em>按用途各选一个。</em></h2>
          </div>
          <p>常见的错是:想找一款"AI 产品经理工具"通吃所有事。好答案是搭一套小栈——一个负责起草、一个负责想点子、一个做笔记、一个跑调研,再加一个保证它们底下的决策不烂掉。下面是每一行里谁胜出,照实说。</p>
        </div>
        <table className="compare-table">
          <thead>
            <tr>
              <th>你要它干什么</th>
              <th>值得用的</th>
              <th className="ours">Draftlize 在哪一格</th>
            </tr>
          </thead>
          <tbody>
            <tr><td>起草 PRD / spec</td><td className="yes">ChatPRD、Notion AI</td><td className="ours partial">不是它的活——它存 PRD 引用的那些决策</td></tr>
            <tr><td>头脑风暴 / 想点子</td><td className="yes">ChatGPT、Claude</td><td className="ours partial">用它们;把结论灌进卡片</td></tr>
            <tr><td>知识库 / 笔记</td><td className="yes">Notion、Obsidian、Mem</td><td className="ours partial">只存决策,不是整个 wiki</td></tr>
            <tr><td>用户调研 / 分析</td><td className="yes">Dovetail、Maze、Amplitude</td><td className="ours no">不是调研工具</td></tr>
            <tr><td>让决策不漂移</td><td className="partial">—— 几乎没人真正在做 ——</td><td className="ours yes">这就是整款产品</td></tr>
            <tr><td>改一个决策,自动标出哪些会塌</td><td className="no">上面没一个能</td><td className="ours yes">依赖图 + 自动 stale 标记</td></tr>
            <tr><td>Claude Code / Cursor 共享同一份上下文</td><td className="no">每个工具各记各的</td><td className="ours yes">一张卡片图,经 MCP 可读可写</td></tr>
          </tbody>
        </table>
      </section>

      <section className="problem">
        <div className="head">
          <h2 className="serif">按类别,<em>照实说。</em></h2>
        </div>
        <div className="problem-grid">
          <div className="problem-col">
            <span className="num">I</span>
            <h3 className="serif">写 PRD 与 spec</h3>
            <p>
              <strong>ChatPRD</strong>——专为 PRD 格式而生,带模板和"教练式"语气,是写作类里最"产品经理形状"的那个。<br/>
              <strong>Notion AI</strong>——如果你的文档本来就在 Notion,想就地起草、不想多装一个工具,它够用。<br/>
              <em>实话:</em>它们能把空白页飞快变成草稿。但它们不会在三周后还记得:当初假设的那个价格,早就改了。
            </p>
          </div>
          <div className="problem-col">
            <span className="num">II</span>
            <h3 className="serif">头脑风暴与聊天</h3>
            <p>
              <strong>ChatGPT</strong>——想点子、语音、快速一次性问题的默认选择,当下很难被超越。<br/>
              <strong>Claude</strong>——擅长长篇、细腻的推理,适合读厚重的调研材料。<br/>
              <em>实话:</em>这两个在这类里都很出色,我们不装看不见。它们的软肋是"留不住"——聊天记录会衰减,你周二定的决策,会悄悄和周五定的那个打架。
            </p>
          </div>
          <div className="problem-col">
            <span className="num">III</span>
            <h3 className="serif">知识库与笔记</h3>
            <p>
              <strong>Notion</strong>——团队 wiki 的默认,灵活、可协作,大家手上都有。<br/>
              <strong>Obsidian</strong>——本地优先、重双链,被习惯"用图思考"的人钟爱。<br/>
              <strong>Mem</strong>——AI 原生、会自我整理的笔记。<br/>
              <em>实话:</em>存知识很好用。但一个 wiki 页面不知道它记录的那条决策什么时候不再成立——它只会原地杵着,理直气壮地错下去。
            </p>
          </div>
          <div className="problem-col">
            <span className="num">IV</span>
            <h3 className="serif">用户调研与分析</h3>
            <p>
              <strong>Dovetail</strong>——AI 辅助归纳访谈与调研。<br/>
              <strong>Maze</strong>——快速、AI 总结的可用性测试。<br/>
              <strong>Amplitude</strong>——带 AI 查询层的产品分析。<br/>
              <em>实话:</em>这一类 Draftlize 完全不沾。要搞懂用户,就用这些——再把学到的东西作为决策喂回去。
            </p>
          </div>
        </div>
      </section>

      <section className="problem" style={{ background: 'var(--paper-1)' }}>
        <div className="head">
          <h2 className="serif">唯一<em>真正属于我们</em>的那一类。</h2>
          <p style={{ maxWidth: 720, marginTop: 12, color: 'var(--ink-1)', fontSize: 16 }}>
            让产品决策不漂移。这是一件很窄的事,也是我们唯一敢认的一类——但上面没一个工具在做,而跳过它,正是一套整整齐齐的 AI 工具栈最后还是各说各话的原因。
          </p>
        </div>
        <div className="problem-grid">
          <div className="problem-col">
            <span className="num">I</span>
            <h3 className="serif">决策落成结构化卡片</h3>
            <p>一份 PRD 草稿、一段聊天结论、一条调研发现——<em>产出</em>都很好,但底下那条决策被埋进了大段文字。Draftlize 把它沉淀成一张卡片:决策、理由、备选、依赖。可按 URL 和 ID 寻址,于是"依据决策 c-04"是一个真链接,而不是模糊的记忆。</p>
          </div>
          <div className="problem-col">
            <span className="num">II</span>
            <h3 className="serif">改一个,依赖它的自动标 stale</h3>
            <p>这正是任何笔记 app、聊天工具、PRD 写作器都不做的事。每张卡片都知道谁依赖它。改掉定价决策,所有建立在旧价格上的 spec 都会自动变 stale——就像构建系统会让改动文件的所有下游全部失效。这就是我们说它让决策"不漂移"的意思。</p>
          </div>
          <div className="problem-col">
            <span className="num">III</span>
            <h3 className="serif">你的 AI agent 能读也能写</h3>
            <p>用 ChatPRD 起草、ChatGPT 想点子、Claude Code 落地实现——每个都各记各的。Draftlize 的卡片是它们共享的那一份上下文,经 MCP 可读<em>也</em>可写,于是给你起草下一份 spec 的 agent,早就知道其他工具定下的那些决策。</p>
          </div>
        </div>
      </section>

      <section className="pullquote">
        <blockquote className="serif">
          产品经理最好的 AI 工具不是某一款——而是一套小栈,再加上一个让底下决策不漂移的东西。
        </blockquote>
        <cite>留着你的写作器、聊天、wiki、调研工具。补上那一层,把它们攥到一起。</cite>
      </section>

      <section className="compare" style={{ paddingTop: 64 }}>
        <div className="head">
          <div>
            <span className="label">常见问题</span>
            <h2 className="serif">FAQ。</h2>
          </div>
        </div>
        <div style={{ maxWidth: 820, fontFamily: "'Inter', sans-serif" }}>
          {[
            { q: '产品经理最好的 AI 工具是哪一款？', a: '没有哪一款——任何点名单一赢家的清单都是在卖东西给你。最好的配置是一套小栈：写 PRD 用 AI 写作器（ChatPRD、Notion AI），想点子用聊天模型（ChatGPT、Claude），记笔记用知识库（Notion、Obsidian），做调研用专门工具（Dovetail、Maze），再加一层决策层（Draftlize）让底下的决策不漂移。' },
            { q: 'ChatGPT 或 Claude 能取代专门的 PM 工具吗？', a: '在头脑风暴和起草上，它们很强、很难被超越。弱点在持久性：聊天记录会衰减，你周二做的决定可能悄悄和周五做的相矛盾。它们负责生成，不负责记住。这正是决策层要补的缺口。' },
            { q: '有没有免费的 PM AI 工具？', a: '有——上面多数工具都有值得先用的免费档，付费前先试。Draftlize 也免费起步：新账号送 $5，按量付费、用多少算多少，没有订阅，余额永不过期。' },
            { q: 'AI 会取代产品经理吗？', a: '不会。AI 把起草、总结、检索这些「打字」的部分成本压到很低，但它不决定该做什么、也不承担取舍。真要说，它反而抬高了清晰、最新的决策的价值——而这恰恰是最难不漂移的部分。' },
          ].map((f, i) => (
            <div key={f.q} style={{ padding: '18px 0', borderTop: i === 0 ? 0 : '1px dashed var(--rule)' }}>
              <h3 className="serif" style={{ fontSize: 18, margin: '0 0 8px', color: 'var(--ink-0)' }}>{f.q}</h3>
              <p style={{ margin: 0, fontSize: 15, lineHeight: 1.8, color: 'var(--ink-1)' }}>{f.a}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="pricing" style={{ paddingTop: 80 }}>
        <div className="head">
          <h2 className="serif">试试这层"决策层",<em>注册送 $5。</em></h2>
        </div>
        <div className="trust-strip">
          <span>新账号送 $5</span>
          <span>按量付费,用多少算多少</span>
          <span>余额永不过期</span>
        </div>
        <div style={{ textAlign: 'center', padding: '40px 24px', border: '1px solid var(--ink-0)', borderTop: 0, background: 'var(--paper-1)' }}>
          <p style={{ fontSize: 16, color: 'var(--ink-1)', margin: '0 0 24px', maxWidth: 640, marginLeft: 'auto', marginRight: 'auto' }}>
            起草、想点子、做调研,继续用你已经爱上的工具。补上 Draftlize,只为了那件它们都不做的事:让底下的决策一直是对的。
          </p>
          <a className="btn-primary" href="/">注册送 $5,开始使用</a>
        </div>
      </section>
    </MarketingShell>
  );
}
